import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BANK_ACCOUNT_KINDS,
  BANK_OPENING_DATE,
  type BankAccountKind,
  type BankAccountRequest,
  type BankAccountResponse,
  formatIban,
  formatMoney,
  ibanError,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

const banksKey = ['treasury', 'banks'] as const;

/**
 * Tesoreria › Banche (07/10/2026): the accounts of K-Rev with bank, IBAN and
 * the balance they start from. The movements of the statements come next.
 */
export function BanksPage() {
  usePageTitle('Banche');
  const canWrite = useCan('tesoreria.write');
  const banks = useQuery({
    queryKey: banksKey,
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const [editing, setEditing] = useState<BankAccountResponse | 'new' | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  useCommands(
    canWrite
      ? [
          {
            key: 'new',
            label: 'Nuovo conto',
            icon: 'plus',
            primary: true,
            onClick: () => setEditing('new'),
          } satisfies Command,
        ]
      : [],
  );
  const all = banks.data ?? [];
  const shown = all.filter((account) => showClosed || account.active);
  const aliasOf = (code: string | null) =>
    code ? (all.find((account) => account.code === code)?.alias ?? code) : null;

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Banche</h1>
          <p className="muted">
            I conti di K-Rev: banche, carte, cassa. Sono i conti della tabella «Conti», qui con
            banca, IBAN e il saldo da cui partono; i saldi aggiornati arriveranno con i movimenti
            degli estratti conto.
          </p>
        </div>
      </div>
      {banks.isPending && <p className="muted">Caricamento…</p>}
      {banks.isError && <p className="error">{banks.error.message}</p>}
      <div className="bank-cards">
        {shown.map((account) => (
          <article key={account.code} className={account.active ? 'card' : 'card inactive'}>
            <div className="row between">
              <h3>{account.alias}</h3>
              <span className="pill muted">{account.kind}</span>
            </div>
            <p className="muted small">
              {[
                account.bankName,
                account.iban && formatIban(account.iban),
                account.bic && `BIC ${account.bic}`,
              ]
                .filter(Boolean)
                .join(' · ') ||
                (account.backingCode
                  ? `Appoggiata a ${aliasOf(account.backingCode)}`
                  : 'Nessun dato bancario')}
            </p>
            {account.backingCode && account.iban && (
              <p className="muted small">Appoggiata a {aliasOf(account.backingCode)}</p>
            )}
            {account.openingBalance !== null && account.openingDate ? (
              <p className="bank-balance">
                <strong>{formatMoney(account.openingBalance)}</strong>
                <small className="muted block">
                  saldo di partenza al {formatDate(account.openingDate)}
                  {account.ledgerAccountCode &&
                    ` · conto del commercialista ${account.ledgerAccountCode}`}
                </small>
              </p>
            ) : (
              <p className="muted small">Saldo di partenza non indicato.</p>
            )}
            {account.statement && (
              <p className="small">
                Saldo dai movimenti:{' '}
                <strong>
                  {account.statement.balance !== null
                    ? formatMoney(account.statement.balance)
                    : '—'}
                </strong>
                {account.statement.balanceDate &&
                  ` al ${formatDate(account.statement.balanceDate)}`}{' '}
                {account.statement.mismatches === 0 ? (
                  <span className="pill ok">torna con la banca</span>
                ) : (
                  <span className="pill warn">
                    non torna in {account.statement.mismatches} giorni
                  </span>
                )}
                <small className="muted block">
                  {account.statement.movements} movimenti
                  {account.statement.startSource === 'banca' && account.statement.startDate
                    ? ` dal ${formatDate(account.statement.startDate)}`
                    : ''}
                </small>
              </p>
            )}
            {account.notes && <p className="small">{account.notes}</p>}
            {!account.active && <span className="pill muted">chiuso</span>}
            <div className="actions">
              {canWrite && (
                <button type="button" onClick={() => setEditing(account)}>
                  Modifica
                </button>
              )}
              {account.kind === 'Banca' && (
                <Link className="button primary" to={`/tesoreria/banche/${account.code}`}>
                  Movimenti
                </Link>
              )}
            </div>
          </article>
        ))}
      </div>
      {all.some((account) => !account.active) && (
        <label className="check small">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(event) => setShowClosed(event.target.checked)}
          />{' '}
          Mostra anche i conti chiusi
        </label>
      )}
      {editing && (
        <BankDialog
          account={editing === 'new' ? null : editing}
          accounts={all}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

interface Form {
  alias: string;
  kind: BankAccountKind;
  bankName: string;
  iban: string;
  bic: string;
  backingCode: string;
  openingBalance: string;
  openingDate: string;
  closedOn: string;
  ledgerAccountCode: string;
  notes: string;
  active: boolean;
  creditLine: string;
  creditLineExpiresOn: string;
  safetyThreshold: string;
  excludedFromSchedule: boolean;
}

function formOf(account: BankAccountResponse | null): Form {
  return {
    alias: account?.alias ?? '',
    kind: account?.kind ?? 'Banca',
    bankName: account?.bankName ?? '',
    iban: account?.iban ? formatIban(account.iban) : '',
    bic: account?.bic ?? '',
    backingCode: account?.backingCode ?? '',
    openingBalance: account?.openingBalance ?? '',
    openingDate: account?.openingDate ?? (account ? '' : BANK_OPENING_DATE),
    closedOn: account?.closedOn ?? '',
    ledgerAccountCode: account?.ledgerAccountCode ?? '',
    notes: account?.notes ?? '',
    active: account?.active ?? true,
    creditLine: account?.creditLine ?? '',
    creditLineExpiresOn: account?.creditLineExpiresOn ?? '',
    safetyThreshold: account?.safetyThreshold ?? '',
    excludedFromSchedule: account?.excludedFromSchedule ?? false,
  };
}

/** New account, or the change of one: the entry of «Conti» and the data of the Tesoreria. */
function BankDialog({
  account,
  accounts,
  onClose,
}: {
  account: BankAccountResponse | null;
  accounts: BankAccountResponse[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<Form>(() => formOf(account));
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const set =
    <K extends keyof Form>(key: K) =>
    (value: Form[K]) =>
      setForm({ ...form, [key]: value });
  const save = useMutation({
    mutationFn: () => {
      const body: BankAccountRequest = {
        alias: form.alias,
        kind: form.kind,
        bankName: form.bankName,
        iban: form.iban,
        bic: form.bic,
        backingCode: form.backingCode || null,
        openingBalance: form.openingBalance.trim() ? form.openingBalance : null,
        openingDate: form.openingBalance.trim() ? form.openingDate || null : null,
        closedOn: form.closedOn || null,
        ledgerAccountCode: form.ledgerAccountCode,
        notes: form.notes,
        active: form.active,
        creditLine: form.creditLine.trim() || '0.00',
        creditLineExpiresOn: form.creditLineExpiresOn || null,
        safetyThreshold: form.safetyThreshold.trim() ? form.safetyThreshold : null,
        excludedFromSchedule: form.excludedFromSchedule,
      };
      return account
        ? apiRequest<BankAccountResponse>(`/treasury/banks/${account.code}`, {
            method: 'PUT',
            body,
          })
        : apiRequest<BankAccountResponse>('/treasury/banks', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: banksKey });
      onClose();
    },
  });
  const ibanProblem = form.iban.trim() ? ibanError(form.iban) : null;
  const backings = accounts.filter(
    (item) => item.kind === 'Banca' && item.active && item.code !== account?.code,
  );
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>{account ? `Conto «${account.alias}»` : 'Nuovo conto'}</h2>
        <div className="form-grid">
          <Field label="Nome (alias)" hint="Il nome breve che usate tutti: «Intesa», «BCC».">
            <input value={form.alias} onChange={(event) => set('alias')(event.target.value)} />
          </Field>
          <Field label="Tipo">
            <select
              value={form.kind}
              onChange={(event) => set('kind')(event.target.value as BankAccountKind)}
            >
              {BANK_ACCOUNT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Banca">
            <input
              value={form.bankName}
              onChange={(event) => set('bankName')(event.target.value)}
            />
          </Field>
          <Field label="IBAN" error={ibanProblem ?? undefined}>
            <input
              value={form.iban}
              autoComplete="off"
              onChange={(event) => set('iban')(event.target.value)}
            />
          </Field>
          <Field label="BIC">
            <input value={form.bic} onChange={(event) => set('bic')(event.target.value)} />
          </Field>
          {form.kind === 'Carta' && (
            <Field label="Conto di appoggio">
              <select
                value={form.backingCode}
                onChange={(event) => set('backingCode')(event.target.value)}
              >
                <option value="">—</option>
                {backings.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.alias}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Saldo di partenza" hint="Positivo se a credito, negativo se scoperto.">
            <DecimalInput
              value={form.openingBalance}
              onChange={set('openingBalance')}
              decimals={[2, 2]}
            />
          </Field>
          <Field label="alla data">
            <input
              type="date"
              value={form.openingDate}
              onChange={(event) => set('openingDate')(event.target.value)}
            />
          </Field>
          <Field
            label="Chiuso il"
            hint="Solo per un conto chiuso: gli estratti si controllano fino a quel giorno."
          >
            <input
              type="date"
              value={form.closedOn}
              onChange={(event) => set('closedOn')(event.target.value)}
            />
          </Field>
          <Field
            label="Conto del commercialista"
            hint="Il codice del mastrino della banca, per confrontare i saldi."
          >
            <input
              value={form.ledgerAccountCode}
              onChange={(event) => set('ledgerAccountCode')(event.target.value)}
            />
          </Field>
          <Field label="Note">
            <input value={form.notes} onChange={(event) => set('notes')(event.target.value)} />
          </Field>
        </div>
        {form.kind === 'Banca' && (
          <fieldset className="stack">
            <legend>Scadenzario</legend>
            <div className="form-grid">
              <Field
                label="Fido accordato"
                hint="Lo scoperto concesso dalla banca; vuoto se non c’è."
              >
                <DecimalInput
                  value={form.creditLine}
                  onChange={set('creditLine')}
                  decimals={[2, 2]}
                />
              </Field>
              <Field label="Scadenza del fido">
                <input
                  type="date"
                  value={form.creditLineExpiresOn}
                  onChange={(event) => set('creditLineExpiresOn')(event.target.value)}
                />
              </Field>
              <Field label="Soglia di sicurezza" hint="Sotto questo saldo lo scadenzario avvisa.">
                <DecimalInput
                  value={form.safetyThreshold}
                  onChange={set('safetyThreshold')}
                  decimals={[2, 2]}
                />
              </Field>
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={form.excludedFromSchedule}
                onChange={(event) => set('excludedFromSchedule')(event.target.checked)}
              />{' '}
              Escluso dallo scadenzario
            </label>
          </fieldset>
        )}
        {account && !account.locked && (
          <label className="check">
            <input
              type="checkbox"
              checked={!form.active}
              onChange={(event) => set('active')(!event.target.checked)}
            />{' '}
            Conto chiuso (resta nello storico, non si usa più)
          </label>
        )}
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !form.alias.trim() || Boolean(ibanProblem)}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
